import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { unlockAudio } from '../utils/audioUnlock';
import {
  Music,
  Play,
  Instagram,
  Youtube,
  Twitch as TwitchIcon,
  Pencil,
  X,
  DollarSign,
  Bell,
  Link,
  AlertTriangle,
  Smartphone,
  Volume2
} from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';
import { useToast } from '../hooks/use-toast';
import UnifiedMusicPlayer from '../components/UnifiedMusicPlayer';
import LiveMobileView from '../components/LiveMobileView';
import LiveMobileOnboardingModal from '../components/LiveMobileOnboardingModal';
import RatingWidget from '../components/RatingWidget';
import PendingPaymentVerifications from '../components/PendingPaymentVerifications';
import { SessionNudge } from '../components/SupportNudge';
import HotSeatPrompt from '../components/HotSeatPrompt';
import { TrackCard, ShimmerTrackCard } from '../components/session';
import { useChallengeActivity } from '../hooks/useChallengeActivity';
import GoLiveGuideTour from '../components/GoLiveGuideTour';
import FloatingGuideButton from '../components/FloatingGuideButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import '../styles/SessionDashboard.css';

const API_URL = '';  // Use relative URLs with proxy

// Priority order for queue sorting: custom (highest) > superskip > skip > regular (lowest)
const SUBMISSION_TYPE_PRIORITY = {
  'custom': 0,
  'superskip': 1,
  'skip': 2,
  'regular': 3,
  undefined: 3,
  null: 3
};

// Helper function to sort queue by priority (custom > superskip > skip > regular)
// Within each tier, maintain FIFO order (earlier submitted_at first)
const sortQueueByPriority = (queue) => {
  if (!queue || !Array.isArray(queue)) return queue;
  return [...queue].sort((a, b) => {
    const priorityA = SUBMISSION_TYPE_PRIORITY[a.submission_type] ?? 3;
    const priorityB = SUBMISSION_TYPE_PRIORITY[b.submission_type] ?? 3;
    
    // First sort by priority (lower number = higher priority)
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Within same tier, sort by queue_position if available
    if (a.queue_position !== undefined && b.queue_position !== undefined) {
      return a.queue_position - b.queue_position;
    }
    
    // Fallback to submitted_at (FIFO - earlier submissions first)
    const timeA = new Date(a.submitted_at || 0).getTime();
    const timeB = new Date(b.submitted_at || 0).getTime();
    return timeA - timeB;
  });
};

// Helper function to format Cash App URL
const formatCashAppUrl = (cashAppValue) => {
  if (!cashAppValue) return null;
  
  // If it&apos;s already a full URL, return as-is
  if (cashAppValue.startsWith('https://cash.app/')) {
    return cashAppValue;
  }
  
  // Remove $ if present and create URL
  const username = cashAppValue.replace('$', '');
  return `https://cash.app/$${username}`;
};

const SessionDashboard = ({ sessionIdOverride, reviewerSlug }) => {
  const { sessionId: paramSessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Use override if provided (for reviewer-scoped public pages), otherwise use URL param
  const sessionId = sessionIdOverride || paramSessionId;
  
  // Check for new_submission query param (passed from redirect after submission)
  const newSubmissionId = searchParams.get('new_submission');
  
  // Get initial tab from URL param (persists across refreshes) - default to 'upcoming'
  const initialTab = searchParams.get('tab') || 'upcoming';
  
  const [session, setSession] = useState(null);
  const [reviewer, setReviewer] = useState(null);
  const [queue, setQueue] = useState({
    spotlight: [],
    upcoming: [],
    finished: [],
    removed: []
  });
  // eslint-disable-next-line no-unused-vars
  const [mySubmissions, setMySubmissions] = useState([]); // For public users - only their submissions
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [isRefreshing, setIsRefreshing] = useState(false); // Track background refresh state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [editingSpotlightName, setEditingSpotlightName] = useState(false);
  const [spotlightName, setSpotlightName] = useState('');
  const [wsConnected, setWsConnected] = useState(false); // WebSocket connection status
  // eslint-disable-next-line no-unused-vars
  const [viewerCount, setViewerCount] = useState(0); // Live viewer count from WebSocket
  
  // Skip/Superskip/Custom notification state for reviewers
  const [skipNotification, setSkipNotification] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const previousQueueRef = useRef([]);
  const audioContextRef = useRef(null);
  const wsRef = useRef(null); // WebSocket reference
  const wsReconnectTimeoutRef = useRef(null); // Reconnection timeout
  const userIsReviewerRef = useRef(false); // Track if current user is the session reviewer
  
  // === STATE SYNC PROTECTION ===
  // Tracks recently added submission IDs to prevent queue_update from removing them
  // This fixes the race condition between new_submission and queue_update events
  const recentlyAddedSubmissionsRef = useRef(new Map()); // id -> timestamp
  const RECENTLY_ADDED_TTL = 5000; // 5 seconds protection window
  
  // Tracks recently removed spotlight IDs to prevent queue_update from re-adding them
  // This fixes the race condition where delete action's optimistic update gets overwritten
  const recentlyRemovedSpotlightRef = useRef(new Map()); // id -> timestamp
  const RECENTLY_REMOVED_TTL = 5000; // 5 seconds protection window
  
  // === PLAYER OVERLAY NOTIFICATIONS (visible on top of big player) ===
  // Left side: new submission count toast
  const [playerSubmissionToast, setPlayerSubmissionToast] = useState(null); // { count: number, timestamp: number }
  // Right side: skip toast notification  
  const [playerSkipToast, setPlayerSkipToast] = useState(null); // { type: string, title: string, timestamp: number }
  const playerSubmissionCountRef = useRef(0); // Track submissions while current track is playing
  const playerSubmissionToastTimerRef = useRef(null); // Timer for auto-hiding submission toast
  
  // === PLAYLIST MODE STATE (for Bangers/Spotlight tab) ===
  const [playlistMode, setPlaylistMode] = useState(false); // Is playlist mode active?
  const [playlistShuffle, setPlaylistShuffle] = useState(false); // Shuffle on/off
  const [playlistQueue, setPlaylistQueue] = useState([]); // The playlist queue (possibly shuffled)
  const [playlistIndex, setPlaylistIndex] = useState(0); // Current position in playlist
  const [playerMountKey, setPlayerMountKey] = useState(0); // Increments to force player remount (fixes Spotify second play bug)
  const tabsRef = useRef(null); // Ref for scrolling to tabs
  
  // === DELETE CONFIRMATION DIALOG STATE ===
  const [deleteConfirmTrack, setDeleteConfirmTrack] = useState(null); // Track to confirm deletion
  
  // === LIVE MOBILE MODE STATE ===
  const [liveMobileMode, setLiveMobileMode] = useState(false); // Is Live Mobile broadcast mode active?
  const [showLiveMobileOnboarding, setShowLiveMobileOnboarding] = useState(false); // Show onboarding modal?
  
  // === LIVE STREAMING PLATFORMS STATE ===
  // Which platforms is the reviewer currently live streaming on?
  const [liveStreamingPlatforms, setLiveStreamingPlatforms] = useState([]);
  
  // === GO LIVE GUIDE TOUR STATE ===
  const [showGoLiveTour, setShowGoLiveTour] = useState(false);
  const [tourResetKey, setTourResetKey] = useState(0); // Forces tour to reset
  
  const handleOpenGuidePage = () => {
    window.open('/go-live-guide', '_blank');
  };
  
  const handleOpenTourFresh = () => {
    // Increment key to force tour component to remount fresh
    setTourResetKey(prev => prev + 1);
    setShowGoLiveTour(true);
  };
  
  // Lock body scroll when Live Mobile is active
  useEffect(() => {
    if (liveMobileMode) {
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    } else {
      // Restore scrolling
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    }
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [liveMobileMode]);
  
  // Notification sound player using Web Audio API
  const playNotificationSound = useCallback((type = 'skip') => {
    try {
      // Create or reuse AudioContext
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      
      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const now = ctx.currentTime;
      
      // Create oscillator for the tone
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Different sounds for different tip types
      if (type === 'custom') {
        // Custom tip: Higher pitched, celebratory double-chime
        oscillator.frequency.setValueAtTime(880, now); // A5
        oscillator.frequency.setValueAtTime(1108, now + 0.1); // C#6
        oscillator.frequency.setValueAtTime(1318, now + 0.2); // E6
        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
        oscillator.start(now);
        oscillator.stop(now + 0.4);
      } else if (type === 'superskip') {
        // Super skip: Strong, attention-grabbing
        oscillator.frequency.setValueAtTime(659, now); // E5
        oscillator.frequency.setValueAtTime(880, now + 0.08); // A5
        gainNode.gain.setValueAtTime(0.35, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
        oscillator.start(now);
        oscillator.stop(now + 0.3);
      } else {
        // Regular skip: Clean, simple notification
        oscillator.frequency.setValueAtTime(523, now); // C5
        oscillator.frequency.setValueAtTime(659, now + 0.1); // E5
        gainNode.gain.setValueAtTime(0.25, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
        oscillator.start(now);
        oscillator.stop(now + 0.25);
      }
    } catch (err) {
      console.log('Could not play notification sound:', err);
    }
  }, []);
  // Real-time notification system for public dashboard (Zone A: Live Submission Feed)
  // FIXED: Changed from array to single object to prevent duplicate cards
  const [liveSubmission, setLiveSubmission] = useState(null); // Single live submission card
  // eslint-disable-next-line no-unused-vars
  const [lastSeenSubmissionId, setLastSeenSubmissionId] = useState(null); // Track by ID to prevent re-triggers
  const [liveCardTimer, setLiveCardTimer] = useState(null); // Reference to clear/restart timer
  const [newSubmissionHandled, setNewSubmissionHandled] = useState(false); // Track if URL param was handled
  
  // Item 2: Zone B - Recent Submissions (rolling top-10 list)
  const [recentSubmissions, setRecentSubmissions] = useState([]); // Top 10 most recent, newest first
  
  // Public ticker state (controlled rotation, max 2 shows per track) - LEGACY, keeping for compatibility
  const [tickerItems, setTickerItems] = useState({}); // Map of submissionId -> {submission, timesShown, lastShownStart}
  const [currentTickerItemId, setCurrentTickerItemId] = useState(null);
  const [tickerStartTime, setTickerStartTime] = useState(null);
  
  // Error handling state - only show error after multiple consecutive failures
  const [fetchError, setFetchError] = useState(null);
  const consecutiveErrorsRef = useRef(0);
  const MAX_CONSECUTIVE_ERRORS = 10; // Show error after 10 consecutive failures (~30 seconds)
  // Increased from 5 to 10 to better handle temporary Cloudflare/origin errors
  // This provides resilience against brief network glitches while still
  // showing real errors when they persist
  
  // Hot Seat prompt state (Millionaire Cafe integration)
  const [hotSeatSubmission, setHotSeatSubmission] = useState(null);
  const [showHotSeatPrompt, setShowHotSeatPrompt] = useState(false);
  
  // Safely parse user data from localStorage - memoized to avoid recreation
  const user = useMemo(() => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return {};
    }
  }, []);
  
  // Calculate isReviewer dynamically based on session data
  const isReviewer = user.id && session?.reviewer_id && user.id === session.reviewer_id;
  
  // === GO LIVE GUIDE TOUR - Only auto-show on FIRST session start ===
  useEffect(() => {
    if (isReviewer && session) {
      const tourCompleted = localStorage.getItem('mcm_go_live_tour_completed');
      const tourAutoShown = localStorage.getItem('mcm_go_live_tour_auto_shown');
      
      // Only auto-show tour if:
      // 1. Tour hasn't been completed AND
      // 2. Tour hasn't been auto-shown before (first time starting session)
      if (!tourCompleted && !tourAutoShown) {
        // Mark that we've auto-shown the tour (won't auto-show again)
        localStorage.setItem('mcm_go_live_tour_auto_shown', 'true');
        
        // Small delay to let the page load first
        const timer = setTimeout(() => {
          setShowGoLiveTour(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [isReviewer, session]);
  
  // === MARCH MADNESS CHALLENGE ACTIVITY TRACKING ===
  // Track playlist and submission events for challenge points
  const challengeActivity = useChallengeActivity(sessionId, isReviewer, session?.reviewer_id);
  const { trackPlay, trackTrackChange, trackSubmissionPlayed, trackReviewCompleted } = challengeActivity;
  // Note: trackPause, trackNext, trackPrevious, trackSubmissionOpened available but not currently used

  // Track if component is mounted for safe state updates
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Persist active tab to URL so it survives page refresh
  useEffect(() => {
    const currentTabParam = searchParams.get('tab');
    if (activeTab !== currentTabParam && activeTab !== 'upcoming') {
      // Only add tab param if not the default (upcoming)
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', activeTab);
      setSearchParams(newParams, { replace: true });
    } else if (activeTab === 'upcoming' && currentTabParam) {
      // Remove tab param if switching back to default
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Check for Hot Seat submission data (Millionaire Cafe integration)
  // This runs once on mount to see if user just submitted a track
  useEffect(() => {
    const newSubmissionParam = searchParams.get('new_submission');
    if (!newSubmissionParam) return;
    
    try {
      const storedData = sessionStorage.getItem('hotSeatSubmission');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        // Only show if submission ID matches and data is fresh (within 5 minutes)
        if (parsed.id === newSubmissionParam && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          setHotSeatSubmission(parsed);
          setShowHotSeatPrompt(true);
          // Clear after reading to prevent showing again on refresh
          sessionStorage.removeItem('hotSeatSubmission');
        }
      }
    } catch (error) {
      console.error('Failed to parse Hot Seat submission data:', error);
    }
  }, [searchParams]);

  // Fetch session data function - wrapped in useCallback to satisfy React hook rules
  // Uses AbortController to cancel stale requests when new polling cycle starts
  const abortControllerRef = useRef(null);
  
  const fetchSessionData = useCallback(async (isInitialLoad = false) => {
    // Cancel any pending request before starting new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    try {
      // Show loading spinner only on initial load, use subtle indicator for refresh
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      // Fetch session details with abort signal
      const sessionRes = await axios.get(`${API_URL}/api/sessions/${sessionId}`, { signal });
      
      // Fetch reviewer details
      const reviewerRes = await axios.get(`${API_URL}/api/reviewers/${sessionRes.data.reviewer_id}`, { signal });
      
      // Check if user is the reviewer
      let user = {};
      try {
        const userData = localStorage.getItem('user');
        user = userData ? JSON.parse(userData) : {};
      } catch (error) {
        console.error('Error parsing user data:', error);
        user = {};
      }
      const userIsReviewer = user.id && user.id === sessionRes.data.reviewer_id;
      
      // Fetch full queue for both reviewers and public users (public can watch live)
      const queueRes = await axios.get(`${API_URL}/api/sessions/${sessionId}/queue`, { signal });
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      
      if (userIsReviewer) {
        // Reviewers see full queue with controls (no ticker filtering)
        setSession(sessionRes.data);
        setReviewer(reviewerRes.data);
        setLiveStreamingPlatforms(reviewerRes.data.live_streaming_platforms || []);
        setQueue(queueRes.data);
      } else {
        // Public users see controlled ticker (one at a time, max 2 shows per track)
        const token = localStorage.getItem('access_token');
        const mySubmissionsRes = await axios.get(
          `${API_URL}/api/sessions/${sessionId}/my-submissions`,
          { signal, ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}) }
        );
        
        // Item 2: Fetch public feed (Zone B - Recent Submissions)
        // This is scoped to reviewer_id + session_id, sorted by submitted_at DESC, limit 10
        const publicFeedRes = await axios.get(`${API_URL}/api/sessions/${sessionId}/public-feed`, { signal });
        
        // Debug log for public feed
        console.log('[DEBUG] Public feed response:', publicFeedRes.data);
        console.log('[DEBUG] Recent submissions count:', publicFeedRes.data?.recent_submissions?.length || 0);
        
        // Only update state if still mounted
        if (!isMountedRef.current) return;
        
        setRecentSubmissions(publicFeedRes.data.recent_submissions || []);
        
        // NOTE: Zone A notification logic moved to dedicated useEffect (lines ~330+)
        // This fetchSessionData function now only handles Zone B (recentSubmissions)
        // to prevent duplicate notification triggers from polling
        
        // Sessions are SACRED - they never return degraded responses
        // If we get data, it's real data
        setSession(sessionRes.data);
        setReviewer(reviewerRes.data);
        setQueue(queueRes.data); // Keep full queue for reference
        setMySubmissions(mySubmissionsRes.data.submissions || []); // User's own tracks for highlighting
      }
      
      setLoading(false);
      setIsRefreshing(false);
      setFetchError(null); // Clear any previous errors
      consecutiveErrorsRef.current = 0; // Reset error counter on success
    } catch (error) {
      console.error('Error fetching session data:', error);
      
      // Only update state if component is still mounted
      if (!isMountedRef.current) return;
      
      // Ignore abort errors (happens when we cancel stale requests)
      if (axios.isCancel(error) || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Request cancelled - this is expected during rapid polling');
        return;
      }
      
      // Increment consecutive error counter
      consecutiveErrorsRef.current += 1;
      
      // For transient errors during polling, only show error UI after multiple consecutive failures
      // This prevents brief network glitches from disrupting the user experience
      const shouldShowError = consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS || loading;
      
      // Graceful error handling for different types of failures
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 404) {
          // 404 should always show immediately - session doesn't exist
          setFetchError({
            type: 'not_found',
            message: 'Session not found or unavailable',
            details: 'This session may have been deleted, or there might be a routing issue. Please check the session URL or contact the reviewer.'
          });
        } else if (error.response.status === 503 || error.response.status === 520) {
          // 503 = database issue, 520 = Cloudflare origin error - both are temporary
          // 520 means origin returned empty/unexpected response (server may have restarted)
          console.warn(`Server temporarily unavailable (${error.response.status}) - (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS}) - will retry`);
          if (shouldShowError) {
            setFetchError({
              type: 'temporary',
              message: 'Connection interrupted',
              details: 'The server connection was temporarily interrupted. Reconnecting automatically...'
            });
          }
          // Don't show error immediately - let polling retry
        } else if (error.response.status === 502 || error.response.status === 504) {
          // 502 = Bad Gateway, 504 = Gateway Timeout - also temporary
          console.warn(`Gateway error (${error.response.status}) - (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS}) - will retry`);
          if (shouldShowError) {
            setFetchError({
              type: 'temporary',
              message: 'Connection timeout',
              details: 'The server is taking longer than expected. Reconnecting automatically...'
            });
          }
        } else if (error.response.status === 403 || error.response.status === 429) {
          // Rate limiting or temporary access issue - only show after multiple failures
          console.warn(`Transient ${error.response.status} error (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS})`);
          if (shouldShowError) {
            setFetchError({
              type: 'forbidden',
              message: 'Session temporarily unavailable',
              details: 'This session is experiencing high traffic. Please wait a moment and try refreshing.'
            });
          }
          // Don't show error immediately - let polling retry
        } else if (shouldShowError) {
          setFetchError({
            type: 'server_error',
            message: 'Server error',
            details: `Unable to load session data (${error.response.status}). Please try again later.`
          });
        }
      } else if (error.request) {
        // Request was made but no response received - network issue
        console.warn(`Network error (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS})`);
        if (shouldShowError) {
          setFetchError({
            type: 'network_error',
            message: 'Network error',
            details: 'Unable to reach the server. Please check your internet connection and try again.'
          });
        }
      } else if (shouldShowError) {
        // Something else went wrong
        setFetchError({
          type: 'unknown_error',
          message: 'Unexpected error',
          details: 'Something went wrong while loading the session. Please refresh the page.'
        });
      }
      
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [sessionId, loading]);

  // =============================================================================
  // 🔌 WEBSOCKET CONNECTION - Scalable real-time updates
  // =============================================================================
  // ARCHITECTURE: WebSocket is the ONLY source of real-time data.
  // NO POLLING - server pushes updates only when something changes.
  // This scales to 1000s of concurrent sessions without API overload.
  //
  // Flow:
  // 1. Initial page load: Fetch /api/sessions/{id}/state for full state
  // 2. Connect WebSocket for real-time updates
  // 3. On reconnect: Rehydrate state from /api/sessions/{id}/state
  // 4. All updates come via WebSocket push (queue_update, session_update, etc.)
  // =============================================================================
  
  // State rehydration function - called on initial load and after reconnection
  const rehydrateState = useCallback(async () => {
    try {
      console.log('🔄 Rehydrating session state...');
      const response = await axios.get(`${API_URL}/api/sessions/${sessionId}/state`);
      
      if (response.data.success && isMountedRef.current) {
        const { session: sessionData, reviewer: reviewerData, queue: queueData } = response.data;
        
        setSession(sessionData);
        setReviewer(reviewerData);
        setLiveStreamingPlatforms(reviewerData?.live_streaming_platforms || []);
        setQueue(queueData);
        setFetchError(null);
        consecutiveErrorsRef.current = 0;
        
        // CRITICAL FIX: Also fetch and set recentSubmissions for public view
        // This ensures the "Recent Submissions" section shows data
        try {
          const publicFeedRes = await axios.get(`${API_URL}/api/sessions/${sessionId}/public-feed`);
          if (publicFeedRes.data?.recent_submissions && isMountedRef.current) {
            setRecentSubmissions(publicFeedRes.data.recent_submissions);
            console.log('✅ Recent submissions loaded:', publicFeedRes.data.recent_submissions.length);
          }
        } catch (feedError) {
          console.warn('⚠️ Could not fetch public feed:', feedError.message);
        }
        
        console.log('✅ State rehydrated successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ State rehydration failed:', error);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        setFetchError({
          type: 'not_found',
          message: 'Session not found',
          details: 'This session may have ended or been deleted.'
        });
      } else {
        consecutiveErrorsRef.current += 1;
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
          setFetchError({
            type: 'temporary',
            message: 'Connection interrupted',
            details: 'Having trouble connecting to the session. Reconnecting...'
          });
        }
      }
      return false;
    }
  }, [sessionId]);

  // WebSocket connection for real-time session updates
  useEffect(() => {
    if (!sessionId) return;
    
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 50; // Increased for long sessions
    const RECONNECT_DELAY_BASE = 300; // Start with 300ms for fast recovery
    const MAX_RECONNECT_DELAY = 10000; // Cap at 10 seconds (was 30s)
    const HEARTBEAT_INTERVAL = 20000; // 20 seconds (was 25s) - detect issues faster
    const HEARTBEAT_TIMEOUT = 8000; // 8 seconds to wait for pong (was 10s)
    
    const connectWebSocket = async () => {
      // Determine WebSocket URL based on current location
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host;
      const wsUrl = `${wsProtocol}//${wsHost}/api/ws/session/${sessionId}`;
      
      console.log(`🔌 Connecting to WebSocket: ${wsUrl}`);
      
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        // ============================================================
        // HEARTBEAT: Enhanced ping/pong with timeout detection
        // ============================================================
        let heartbeatInterval = null;
        let heartbeatTimeout = null;
        let lastPongReceived = Date.now();
        
        const startHeartbeat = () => {
          if (heartbeatInterval) clearInterval(heartbeatInterval);
          if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
          
          heartbeatInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try {
                ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
                console.log('💓 Heartbeat sent');
                
                // Set timeout to detect dead connection
                if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
                heartbeatTimeout = setTimeout(() => {
                  const timeSinceLastPong = Date.now() - lastPongReceived;
                  if (timeSinceLastPong > HEARTBEAT_INTERVAL + HEARTBEAT_TIMEOUT) {
                    console.warn('💔 Heartbeat timeout - no pong received, reconnecting...');
                    ws.close(4000, 'Heartbeat timeout');
                  }
                }, HEARTBEAT_TIMEOUT);
              } catch (e) {
                console.error('💔 Heartbeat failed:', e);
                ws.close(4001, 'Heartbeat send failed');
              }
            }
          }, HEARTBEAT_INTERVAL);
        };
        
        const stopHeartbeat = () => {
          if (heartbeatInterval) {
            clearInterval(heartbeatInterval);
            heartbeatInterval = null;
          }
          if (heartbeatTimeout) {
            clearTimeout(heartbeatTimeout);
            heartbeatTimeout = null;
          }
        };
        
        // Track pong receipt
        const handlePong = () => {
          lastPongReceived = Date.now();
          if (heartbeatTimeout) {
            clearTimeout(heartbeatTimeout);
            heartbeatTimeout = null;
          }
        };
        
        ws.onopen = async () => {
          console.log('🔌 WebSocket connected');
          setWsConnected(true);
          lastPongReceived = Date.now(); // Initialize pong tracker
          reconnectAttempts = 0; // Reset on successful connection
          consecutiveErrorsRef.current = 0;
          
          // Start heartbeat
          startHeartbeat();
          
          // Clear any existing temporary error
          if (fetchError?.type === 'temporary') {
            setFetchError(null);
          }
          
          // Rehydrate state after reconnection (ensures we have latest data)
          // Always rehydrate to ensure we have fresh data
          await rehydrateState();
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📡 WebSocket message:', data.type);
            
            switch (data.type) {
              case 'connected':
                console.log('✅ WebSocket confirmed connected to session');
                break;
                
              case 'queue_update':
                // Server pushed queue update - THIS IS THE CANONICAL SOURCE
                if (data.queue && isMountedRef.current) {
                  console.log('📥 Queue update received, total items:', data.queue.length);
                  
                  // Transform flat queue to organized structure
                  // IMPORTANT: Spotlight tracks should NOT appear in finished (even though they have status='reviewed')
                  let spotlight = data.queue.filter(s => s.spotlight === true || s.is_spotlighted === true);
                  const upcoming = data.queue.filter(s => !s.spotlight && !s.is_spotlighted && s.status === 'pending');
                  const finished = data.queue.filter(s => 
                    (s.status === 'reviewed' || s.status === 'approved') && 
                    !s.spotlight && !s.is_spotlighted  // Exclude spotlight tracks from finished
                  );
                  const removed = data.queue.filter(s => s.status === 'removed' || s.status === 'skipped');
                  
                  // ============================================================
                  // RACE CONDITION PROTECTION: Filter out recently removed spotlight
                  // ============================================================
                  const now = Date.now();
                  
                  // Clean up expired entries first
                  for (const [id, timestamp] of recentlyRemovedSpotlightRef.current.entries()) {
                    if (now - timestamp > RECENTLY_REMOVED_TTL) {
                      recentlyRemovedSpotlightRef.current.delete(id);
                    }
                  }
                  
                  // Filter out any spotlight tracks that were recently removed by the user
                  const filteredSpotlightCount = spotlight.length;
                  spotlight = spotlight.filter(track => {
                    const wasRecentlyRemoved = recentlyRemovedSpotlightRef.current.has(track.id);
                    if (wasRecentlyRemoved) {
                      console.log('🛡️ Filtering out recently removed spotlight from WebSocket:', track.id);
                    }
                    return !wasRecentlyRemoved;
                  });
                  
                  if (filteredSpotlightCount !== spotlight.length) {
                    console.log(`📊 Spotlight filtered: ${filteredSpotlightCount} → ${spotlight.length} (protected recent removals)`);
                  }
                  
                  // Log queue breakdown for debugging
                  console.log('📊 Queue breakdown:', {
                    spotlight: spotlight.length,
                    upcoming: upcoming.length,
                    finished: finished.length,
                    removed: removed.length
                  });
                  
                  // CRITICAL FIX: Use functional state update to prevent state corruption
                  // Preserve finished tracks if the new data would incorrectly wipe them out
                  setQueue(prevQueue => {
                    // ============================================================
                    // RACE CONDITION PROTECTION: Preserve recently added submissions
                    // ============================================================
                    let mergedUpcoming = [...upcoming];
                    
                    // Check if any recently added submissions are missing from the server's queue
                    for (const [id, timestamp] of recentlyAddedSubmissionsRef.current.entries()) {
                      if (now - timestamp < RECENTLY_ADDED_TTL) {
                        // This submission was added recently - ensure it's in upcoming
                        const existsInUpcoming = upcoming.some(t => t.id === id);
                        const existsInPrevUpcoming = (prevQueue.upcoming || []).find(t => t.id === id);
                        
                        if (!existsInUpcoming && existsInPrevUpcoming) {
                          console.log('🛡️ Preserving recently added submission from queue_update race:', id);
                          mergedUpcoming.push(existsInPrevUpcoming);
                        }
                      }
                    }
                    
                    // CRITICAL: Sort merged upcoming by priority to ensure correct order
                    mergedUpcoming = sortQueueByPriority(mergedUpcoming);
                    
                    const hadFinished = (prevQueue.finished || []).length > 0;
                    const newFinishedCount = finished.length;
                    
                    // PROTECTION: If we had finished tracks and now got 0, but total queue has items
                    // This indicates a potential data race or filtering issue - PRESERVE EXISTING
                    if (hadFinished && newFinishedCount === 0 && data.queue.length > 0) {
                      console.warn('⚠️ Finished tracks would go to 0 unexpectedly - PRESERVING EXISTING STATE');
                      // Check if the tracks might have wrong status
                      const possibleFinished = data.queue.filter(s => 
                        s.status === 'reviewed' || s.status === 'approved' || s.status === 'finished'
                      );
                      console.log('🔍 Possible finished tracks (all reviewed/approved/finished):', possibleFinished.length);
                      
                      // Use existing finished if server sent 0 but we have tracks
                      // This prevents the glitch while still updating other queues
                      return { 
                        spotlight, 
                        upcoming: mergedUpcoming, 
                        finished: prevQueue.finished, // KEEP EXISTING
                        removed 
                      };
                    }
                    
                    // Normal update - server data is valid
                    return { spotlight, upcoming: mergedUpcoming, finished, removed };
                  });
                  
                  // DO NOT update recentSubmissions from WebSocket data!
                  // The WebSocket queue includes spotlight tracks from old sessions.
                  // recentSubmissions should ONLY be updated from /public-feed API
                  // which properly filters by session_id.
                }
                break;
                
              case 'new_submission':
                // New submission added - show notification IMMEDIATELY
                if (data.submission && isMountedRef.current) {
                  console.log('🎵 New submission received:', data.submission.track_title || data.submission.title);
                  
                  // ============================================================
                  // TRACK RECENTLY ADDED - Prevents queue_update race condition
                  // ============================================================
                  recentlyAddedSubmissionsRef.current.set(data.submission.id, Date.now());
                  // Clean up old entries
                  const now = Date.now();
                  for (const [id, timestamp] of recentlyAddedSubmissionsRef.current.entries()) {
                    if (now - timestamp > RECENTLY_ADDED_TTL) {
                      recentlyAddedSubmissionsRef.current.delete(id);
                    }
                  }
                  
                  // ============================================================
                  // IMMEDIATE COUNTER UPDATE - Must happen synchronously
                  // ============================================================
                  // Update queue state immediately to sync counters
                  // FIX: Insert new track in correct priority position instead of appending
                  setQueue(prevQueue => {
                    const newTrack = {
                      id: data.submission.id,
                      title: data.submission.title || data.submission.track_title,
                      track_title: data.submission.track_title || data.submission.title,
                      artist: data.submission.artist,
                      platform: data.submission.platform,
                      submission_type: data.submission.submission_type,
                      queue_position: data.submission.queue_position,
                      status: 'pending',
                      submitted_at: data.submission.submitted_at || new Date().toISOString()
                    };
                    
                    // Add to upcoming if not already there
                    const existingIds = new Set((prevQueue.upcoming || []).map(t => t.id));
                    if (!existingIds.has(newTrack.id)) {
                      console.log('📥 Adding new track to upcoming queue with priority sort');
                      // Add track and re-sort entire queue by priority
                      const updatedUpcoming = sortQueueByPriority([...(prevQueue.upcoming || []), newTrack]);
                      return {
                        ...prevQueue,
                        upcoming: updatedUpcoming
                      };
                    }
                    return prevQueue;
                  });
                  
                  // ============================================================
                  // PAID SUBMISSION NOTIFICATION - Show immediately
                  // ============================================================
                  const subType = data.submission.submission_type;
                  if (['skip', 'superskip', 'custom'].includes(subType)) {
                    const typeLabel = subType === 'custom' ? 'CUSTOM TIP' : 
                                     subType === 'superskip' ? 'SUPERSKIP' : 'SKIP';
                    const emoji = subType === 'custom' ? '💚' : 
                                 subType === 'superskip' ? '🔴' : '🟡';
                    
                    // Use flushSync to ensure immediate DOM update
                    flushSync(() => {
                      playNotificationSound(subType);
                      setSkipNotification({
                        id: data.submission.id,
                        type: subType,
                        label: typeLabel,
                        emoji: emoji,
                        title: data.submission.title || data.submission.track_title,
                        artist: data.submission.artist,
                        timestamp: Date.now()
                      });
                      setNotificationCount(prev => prev + 1);
                      
                      // Mark as notified to prevent duplicate from queue_update
                      notifiedSubmissionsRef.current.add(data.submission.id);
                    });
                    
                    // === PLAYER OVERLAY: Show skip toast on top of player (right side) ===
                    setPlayerSkipToast({
                      type: subType,
                      label: typeLabel,
                      emoji: emoji,
                      title: data.submission.title || data.submission.track_title,
                      artist: data.submission.artist,
                      timestamp: Date.now()
                    });
                    // Auto-hide player skip toast after 8 seconds (longer so reviewer can see it)
                    setTimeout(() => setPlayerSkipToast(null), 8000);
                    
                    // Auto-hide notification after 8 seconds
                    setTimeout(() => setSkipNotification(null), 8000);
                  } else if (userIsReviewerRef.current) {
                    // Play simple sound for free submissions (reviewers only)
                    playNotificationSound('submission');
                    // Also mark free submissions as notified
                    notifiedSubmissionsRef.current.add(data.submission.id);
                  }
                  
                  // === PLAYER OVERLAY: Update submission count toast (left side) ===
                  // This shows for ALL submissions (free and paid) while a track is playing
                  if (userIsReviewerRef.current) {
                    // Clear any existing timer so it doesn't hide prematurely
                    if (playerSubmissionToastTimerRef.current) {
                      clearTimeout(playerSubmissionToastTimerRef.current);
                    }
                    
                    // Increment count (starts fresh at 1 if counter was reset)
                    playerSubmissionCountRef.current += 1;
                    
                    setPlayerSubmissionToast({
                      count: playerSubmissionCountRef.current,
                      timestamp: Date.now()
                    });
                    
                    // Auto-hide after 6 seconds and reset counter for next batch
                    playerSubmissionToastTimerRef.current = setTimeout(() => {
                      setPlayerSubmissionToast(null);
                      playerSubmissionCountRef.current = 0;
                      playerSubmissionToastTimerRef.current = null;
                    }, 6000);
                  }
                  
                  // Show live submission card (Zone A) for public viewers
                  if (!userIsReviewerRef.current) {
                    setLiveSubmission({
                      id: data.submission.id,
                      artist: data.submission.artist,
                      title: data.submission.title || data.submission.track_title,
                      platform: data.submission.platform,
                      platform_icon: data.submission.platform_icon,
                      submitted_at: data.submission.submitted_at
                    });
                    
                    // Also add to recent submissions list (Zone B) for immediate visibility
                    setRecentSubmissions(prev => {
                      const newSubmission = {
                        id: data.submission.id,
                        artist: data.submission.artist,
                        title: data.submission.title || data.submission.track_title,
                        track_title: data.submission.track_title || data.submission.title,
                        platform: data.submission.platform,
                        platform_icon: data.submission.platform_icon,
                        submitted_at: data.submission.submitted_at || new Date().toISOString(),
                        status: 'pending'
                      };
                      // Add to front, remove duplicates, keep max 10
                      const filtered = prev.filter(s => s.id !== newSubmission.id);
                      return [newSubmission, ...filtered].slice(0, 10);
                    });
                    
                    // Clear after 7 seconds
                    if (liveCardTimer) clearTimeout(liveCardTimer);
                    const timer = setTimeout(() => setLiveSubmission(null), 7000);
                    setLiveCardTimer(timer);
                  }
                }
                break;
                
              case 'session_update':
                // Session state changed (submissions_enabled, skips_only_mode, etc.)
                if (data.session && isMountedRef.current) {
                  setSession(prev => ({ ...prev, ...data.session }));
                }
                break;
                
              case 'session_ended':
                // Session has ended
                console.log('🛑 Session ended by reviewer');
                setFetchError({
                  type: 'session_ended',
                  message: 'Session has ended',
                  details: 'The reviewer has ended this session. Thank you for watching!'
                });
                break;
                
              case 'viewer_count':
                // Update live viewer count
                if (typeof data.count === 'number') {
                  setViewerCount(data.count);
                }
                break;
                
              case 'ping':
                // Server ping - respond with pong to keep connection alive
                ws.send(JSON.stringify({ type: 'pong' }));
                break;
                
              case 'pong':
                // Response to our ping - connection is alive
                console.log('💚 Pong received - connection healthy');
                handlePong();
                break;
              
              case 'ping':
                // Server-initiated ping — respond with pong to keep alive
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(JSON.stringify({ type: 'pong' }));
                }
                handlePong(); // Also counts as proof of life
                break;
                
              default:
                console.log('📡 Unknown WebSocket message type:', data.type);
            }
          } catch (parseError) {
            console.error('Failed to parse WebSocket message:', parseError);
          }
        };
        
        ws.onclose = (event) => {
          console.log(`🔌 WebSocket closed: code=${event.code}, reason=${event.reason || 'No reason'}`);
          setWsConnected(false);
          wsRef.current = null;
          stopHeartbeat(); // Stop heartbeat on close
          
          // Don't reconnect if session ended or page is unmounting
          if (event.code === 1000 || !isMountedRef.current) {
            console.log('🔌 Clean close, not reconnecting');
            return;
          }
          
          // Attempt to reconnect with exponential backoff (capped)
          if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS && isMountedRef.current) {
            // First attempt is instant, then exponential backoff
            const delay = reconnectAttempts === 0 ? 0 : Math.min(
              RECONNECT_DELAY_BASE * Math.pow(1.3, reconnectAttempts - 1),
              MAX_RECONNECT_DELAY
            );
            console.log(`🔄 Reconnecting ${delay === 0 ? 'immediately' : `in ${Math.round(delay/1000)}s`} (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
            
            wsReconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttempts++;
              connectWebSocket();
            }, delay);
          } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            console.error('❌ WebSocket max reconnection attempts reached');
            setFetchError({
              type: 'connection_failed',
              message: 'Connection lost',
              details: 'Unable to maintain connection. Please refresh the page.'
            });
          }
        };
        
        ws.onerror = (error) => {
          console.error('🔌 WebSocket error:', error);
          // Don't set wsConnected=false here - let onclose handle it
        };
        
      } catch (wsError) {
        console.error('Failed to create WebSocket:', wsError);
        // Attempt reconnect on creation failure
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS && isMountedRef.current) {
          const delay = reconnectAttempts === 0 ? 0 : Math.min(
            RECONNECT_DELAY_BASE * Math.pow(1.3, reconnectAttempts - 1),
            MAX_RECONNECT_DELAY
          );
          setTimeout(() => {
            reconnectAttempts++;
            connectWebSocket();
          }, delay);
        }
      }
    };
    
    // Start WebSocket connection
    connectWebSocket();
    
    // Reconnect immediately when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current) {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('👁️ Tab visible again — reconnecting WebSocket');
          reconnectAttempts = 0;
          connectWebSocket();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Reconnect immediately when network comes back online
    const handleOnline = () => {
      if (isMountedRef.current) {
        console.log('🌐 Network back online — reconnecting WebSocket');
        reconnectAttempts = 0;
        if (wsReconnectTimeoutRef.current) {
          clearTimeout(wsReconnectTimeoutRef.current);
        }
        connectWebSocket();
      }
    };
    window.addEventListener('online', handleOnline);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, [sessionId, playNotificationSound]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Track if user is reviewer (for WebSocket message handling)
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : {};
      userIsReviewerRef.current = user.id && session?.reviewer_id && user.id === session.reviewer_id;
    } catch {
      userIsReviewerRef.current = false;
    }
  }, [session?.reviewer_id]);

  // =============================================================================
  // INITIAL DATA LOAD (one-time, then WebSocket takes over)
  // =============================================================================
  useEffect(() => {
    if (!sessionId) return;
    
    const loadInitialData = async () => {
      setLoading(true);
      const success = await rehydrateState();
      setLoading(false);
      
      if (!success) {
        // Fallback to legacy fetch if state endpoint fails
        await fetchSessionData(true);
      }
    };
    
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // CRITICAL: Session heartbeat to prevent auto-offline
  // Sends heartbeat every 30 seconds to keep session alive even without submissions
  // NOTE: This runs for ANY user viewing the session dashboard to ensure session stays alive
  // The backend heartbeat only updates if session exists and is active
  useEffect(() => {
    if (!sessionId) return;
    
    // Track consecutive heartbeat failures for resilience
    let consecutiveFailures = 0;
    const MAX_HEARTBEAT_FAILURES = 5;
    
    const sendHeartbeat = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/sessions/${sessionId}/heartbeat`);
        if (response.data?.success) {
          console.log('💓 Session heartbeat sent');
          consecutiveFailures = 0; // Reset on success
        } else {
          // Session might not exist or be inactive - this is normal for public viewers
          console.log('💔 Heartbeat: session inactive or not found');
        }
      } catch (err) {
        consecutiveFailures++;
        console.warn(`Heartbeat failed (${consecutiveFailures}/${MAX_HEARTBEAT_FAILURES}):`, err.message);
        
        // If too many failures, log but don't crash - let polling continue
        if (consecutiveFailures >= MAX_HEARTBEAT_FAILURES) {
          console.error('⚠️ Heartbeat service degraded - multiple consecutive failures');
        }
      }
    };
    
    // Send initial heartbeat immediately
    sendHeartbeat();
    
    // Then every 30 seconds (aligns with auto-offline check interval)
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);
    
    return () => clearInterval(heartbeatInterval);
  }, [sessionId]); // Removed isReviewer dependency - heartbeat runs for all viewers
  
  // Skip/Superskip/Custom notification detection for REVIEWERS
  // Only triggers for GENUINELY NEW submissions, not queue reorganizations
  const notifiedSubmissionsRef = useRef(new Set());
  const initializedNotificationsRef = useRef(false);
  
  useEffect(() => {
    if (!isReviewer || !queue.upcoming) return;
    
    const currentUpcoming = queue.upcoming || [];
    
    // On first load, mark all existing submissions as "already notified"
    // so we don't show notifications for tracks that were already there
    if (!initializedNotificationsRef.current && currentUpcoming.length > 0) {
      currentUpcoming.forEach(track => {
        notifiedSubmissionsRef.current.add(track.id);
      });
      initializedNotificationsRef.current = true;
      console.log('[Notifications] Initialized with existing tracks:', currentUpcoming.length);
      return; // Don't process notifications on first load
    }
    
    // Find paid submissions that we haven't notified about yet
    const newPaidSubmissions = currentUpcoming.filter(track => 
      ['skip', 'superskip', 'custom'].includes(track.submission_type) &&
      !notifiedSubmissionsRef.current.has(track.id)
    );
    
    // Show notification for each genuinely new paid submission
    if (newPaidSubmissions.length > 0) {
      newPaidSubmissions.forEach(submission => {
        // Mark as notified so we don't show again
        notifiedSubmissionsRef.current.add(submission.id);
        
        const typeLabel = submission.submission_type === 'custom' ? 'CUSTOM TIP' : 
                         submission.submission_type === 'superskip' ? 'SUPERSKIP' : 'SKIP';
        const emoji = submission.submission_type === 'custom' ? '💚' : 
                     submission.submission_type === 'superskip' ? '🔴' : '🟡';
        
        const notificationData = {
          id: submission.id,
          type: submission.submission_type,
          label: typeLabel,
          emoji: emoji,
          title: submission.title,
          artist: submission.artist,
          timestamp: Date.now()
        };
        
        requestAnimationFrame(() => {
          playNotificationSound(submission.submission_type);
          setSkipNotification(notificationData);
          setNotificationCount(prev => prev + 1);
        });
        
        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setSkipNotification(null);
        }, 5000);
      });
    }
  }, [queue.upcoming, isReviewer, playNotificationSound]);
  
  // Public ticker rotation logic (runs every second for smooth transitions)
  useEffect(() => {
    // Only run ticker for public users (not reviewers)
    const userIsReviewer = user.id && user.id === session?.reviewer_id;
    if (userIsReviewer) return;
    
    const DISPLAY_TIME = 10000; // 10 seconds per item
    const MAX_SHOWS = 2; // Each track shown max 2 times
    
    const tickerInterval = setInterval(() => {
      const now = Date.now();
      
      // Get all eligible items (timesShown < 2, from current session only)
      const eligibleItems = Object.entries(tickerItems)
        // eslint-disable-next-line no-unused-vars
        .filter(([id, item]) => 
          item.timesShown < MAX_SHOWS && 
          item.submission.session_id === sessionId
        )
        .map(([itemId, item]) => ({ id: itemId, ...item }));
      
      // If no current item is showing, pick the next eligible one
      if (!currentTickerItemId || !tickerStartTime) {
        if (eligibleItems.length > 0) {
          const nextItem = eligibleItems[0];
          setCurrentTickerItemId(nextItem.id);
          setTickerStartTime(now);
          
          // Increment timesShown for this item
          setTickerItems(prev => ({
            ...prev,
            [nextItem.id]: {
              ...prev[nextItem.id],
              timesShown: prev[nextItem.id].timesShown + 1,
              lastShownStart: now
            }
          }));
        }
        return;
      }
      
      // Check if current item has been shown for 10 seconds
      if (now - tickerStartTime >= DISPLAY_TIME) {
        // Move to next eligible item
        const nextEligibleItems = eligibleItems.filter(item => item.id !== currentTickerItemId);
        
        if (nextEligibleItems.length > 0) {
          const nextItem = nextEligibleItems[0];
          setCurrentTickerItemId(nextItem.id);
          setTickerStartTime(now);
          
          // Increment timesShown for the next item
          setTickerItems(prev => ({
            ...prev,
            [nextItem.id]: {
              ...prev[nextItem.id],
              timesShown: prev[nextItem.id].timesShown + 1,
              lastShownStart: now
            }
          }));
        } else {
          // No more eligible items, clear ticker
          setCurrentTickerItemId(null);
          setTickerStartTime(null);
        }
      }
    }, 1000); // Run every second for smooth transitions
    
    return () => clearInterval(tickerInterval);
  }, [currentTickerItemId, tickerStartTime, tickerItems, sessionId, session, user]);

  // =============================================================================
  // Handle new_submission URL param (redirect from submission page)
  // WebSocket handles all other new submission notifications
  // =============================================================================
  useEffect(() => {
    if (!session || !reviewer || isReviewer) return;
    if (!newSubmissionId || newSubmissionHandled) return;
    
    // Mark as handled immediately to prevent re-trigger
    setNewSubmissionHandled(true);
    
    // Show live card for the submission that was just made
    console.log('[ZoneA] Showing submission from redirect:', newSubmissionId);
    
    // Try to find submission details in our queue
    const allSubmissions = [
      ...(queue.upcoming || []),
      ...(queue.finished || []),
      ...(queue.spotlight || [])
    ];
    const newSub = allSubmissions.find(s => s.id === newSubmissionId);
    
    if (newSub) {
      setLiveSubmission({
        id: newSub.id,
        artist: newSub.artist,
        title: newSub.title || newSub.track_title,
        platform: newSub.platform,
        platform_icon: newSub.platform_icon,
        submitted_at: newSub.submitted_at
      });
      
      // Clear after 7 seconds
      const timer = setTimeout(() => {
        setLiveSubmission(null);
        console.log('[ZoneA]', newSubmissionId, 'cleared (7s timer)');
      }, 7000);
      setLiveCardTimer(timer);
    }
    
    // Clean up URL param
    searchParams.delete('new_submission');
    setSearchParams(searchParams, { replace: true });
    
    return () => {
      if (liveCardTimer) clearTimeout(liveCardTimer);
    };
  }, [session, reviewer, isReviewer, newSubmissionId, newSubmissionHandled, queue, searchParams, setSearchParams, liveCardTimer]);

  const toggleSubmissions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Optimistic update - immediately toggle UI
      const newState = !session.submissions_enabled;
      setSession(prev => ({ ...prev, submissions_enabled: newState }));
      
      await axios.post(
        `${API_URL}/api/sessions/${sessionId}/toggle-submissions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // WebSocket will broadcast the update to other clients
    } catch (error) {
      console.error('Error toggling submissions:', error);
      // Revert optimistic update on error
      setSession(prev => ({ ...prev, submissions_enabled: !prev.submissions_enabled }));
      toast({ title: "Error", description: "Failed to toggle submissions", variant: "destructive" });
    }
  };

  const toggleSkipsOnly = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Optimistic update - immediately toggle UI
      const newState = !session.skips_only_mode;
      setSession(prev => ({ ...prev, skips_only_mode: newState }));
      
      await axios.post(
        `${API_URL}/api/sessions/${sessionId}/toggle-skips-only`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // WebSocket will broadcast the update to other clients
    } catch (error) {
      console.error('Error toggling skips only:', error);
      // Revert optimistic update on error
      setSession(prev => ({ ...prev, skips_only_mode: !prev.skips_only_mode }));
      toast({ title: "Error", description: "Failed to toggle skips mode", variant: "destructive" });
    }
  };

  // Toggle which platforms the reviewer is live streaming on
  const toggleLiveStreamingPlatform = async (platform) => {
    try {
      const token = localStorage.getItem('access_token');
      const reviewerId = reviewer?.id || reviewer?._id;
      
      if (!reviewerId) return;
      
      // Optimistic update
      const newPlatforms = liveStreamingPlatforms.includes(platform)
        ? liveStreamingPlatforms.filter(p => p !== platform)
        : [...liveStreamingPlatforms, platform];
      
      setLiveStreamingPlatforms(newPlatforms);
      
      await axios.put(
        `${API_URL}/api/reviewers/${reviewerId}/live-platforms`,
        { live_streaming_platforms: newPlatforms },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error toggling live platform:', error);
      // Revert on error
      setLiveStreamingPlatforms(reviewer?.live_streaming_platforms || []);
    }
  };

  const updateSpotlightName = async (newName) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${API_URL}/api/sessions/${sessionId}/settings`,
        { spotlight_queue_name: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSession({ ...session, spotlight_queue_name: newName });
      setEditingSpotlightName(false);
    } catch (error) {
      console.error('Error updating spotlight name:', error);
    }
  };

  const addToSpotlight = async (submissionId) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Track review completion for March Madness Challenge
      trackReviewCompleted(submissionId, 'spotlight');
      
      // Optimistic update: immediately update UI before API call completes
      const trackToSpotlight = queue.upcoming.find(t => t.id === submissionId) || 
                               queue.finished.find(t => t.id === submissionId);
      
      // Check if this track was already reviewed (to match backend logic)
      const wasAlreadyReviewed = trackToSpotlight?.status === 'reviewed';
      
      if (trackToSpotlight) {
        // Mark track as spotlighted immediately and move to spotlight
        const updatedTrack = { ...trackToSpotlight, is_spotlighted: true, status: 'reviewed' };
        setQueue(prev => ({
          ...prev,
          spotlight: [...prev.spotlight, updatedTrack],
          upcoming: prev.upcoming.filter(t => t.id !== submissionId), // Remove from upcoming
          finished: prev.finished.filter(t => t.id !== submissionId)  // Remove from finished
        }));
        
        // Optimistically increment review count if not already reviewed
        if (!wasAlreadyReviewed) {
          setReviewer(prev => prev ? {
            ...prev,
            totalReviews: (prev.totalReviews || 0) + 1
          } : prev);
        }
      }
      
      // Then make the API call
      await axios.post(
        `${API_URL}/api/sessions/${sessionId}/add-to-spotlight`,
        { submission_id: submissionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // WebSocket will broadcast queue_update - no need to refetch
      // Optimistic update already handled above
    } catch (error) {
      console.error('Error adding to spotlight:', error);
      // Revert optimistic update on error by rehydrating state
      rehydrateState();
    }
  };

  // eslint-disable-next-line no-unused-vars
  const removeFromSpotlight = async (submissionId) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Track this removal to prevent WebSocket from re-adding it
      recentlyRemovedSpotlightRef.current.set(submissionId, Date.now());
      console.log('🛡️ Tracking removed spotlight:', submissionId);
      
      // Optimistic update: immediately remove from spotlight
      setQueue(prev => ({
        ...prev,
        spotlight: prev.spotlight.filter(t => t.id !== submissionId)
      }));
      
      // Then make the API call
      await axios.post(
        `${API_URL}/api/sessions/${sessionId}/remove-from-spotlight`,
        { submission_id: submissionId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      // WebSocket will broadcast queue_update - no need to refetch
    } catch (error) {
      console.error('Error removing from spotlight:', error);
      // Clear the protection since the API call failed
      recentlyRemovedSpotlightRef.current.delete(submissionId);
      // Revert optimistic update on error by rehydrating state
      rehydrateState();
    }
  };

  const moveToFinished = async (submissionId) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Track review completion for March Madness Challenge
      trackReviewCompleted(submissionId, 'finished');
      
      // Optimistic update: immediately move track to finished
      const trackToFinish = queue.upcoming.find(t => t.id === submissionId) || 
                            queue.spotlight.find(t => t.id === submissionId);
      
      // Check if this track was already reviewed (to match backend logic)
      const wasAlreadyReviewed = trackToFinish?.status === 'reviewed';
      
      if (trackToFinish) {
        setQueue(prev => ({
          ...prev,
          finished: [...prev.finished, { ...trackToFinish, status: 'reviewed' }],
          upcoming: prev.upcoming.filter(t => t.id !== submissionId)
        }));
        
        // Optimistically increment review count if not already reviewed
        if (!wasAlreadyReviewed) {
          setReviewer(prev => prev ? {
            ...prev,
            totalReviews: (prev.totalReviews || 0) + 1
          } : prev);
        }
      }
      
      // Then make the API call
      await axios.post(
        `${API_URL}/api/sessions/${sessionId}/move-to-finished`,
        { submission_id: submissionId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      // WebSocket will broadcast queue_update - no need to refetch
    } catch (error) {
      console.error('Error moving to finished:', error);
      // Revert optimistic update on error by rehydrating state
      rehydrateState();
    }
  };

  const moveToRemoved = async (submissionId) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Find the track object from any queue
      const trackToRemove = 
        queue.upcoming.find(t => t.id === submissionId) ||
        queue.spotlight.find(t => t.id === submissionId) ||
        queue.finished.find(t => t.id === submissionId);
      
      // Track this removal to prevent WebSocket from re-adding it
      // This protects the optimistic update from being overwritten
      const wasInSpotlight = queue.spotlight.some(t => t.id === submissionId);
      if (wasInSpotlight) {
        recentlyRemovedSpotlightRef.current.set(submissionId, Date.now());
        console.log('🛡️ Tracking removed spotlight:', submissionId);
      }
      
      // Optimistic update: immediately move track to removed queue
      setQueue(prev => ({
        ...prev,
        removed: trackToRemove ? [...prev.removed, { ...trackToRemove, status: 'removed' }] : prev.removed,
        upcoming: prev.upcoming.filter(t => t.id !== submissionId),
        spotlight: prev.spotlight.filter(t => t.id !== submissionId),
        finished: prev.finished.filter(t => t.id !== submissionId)
      }));
      
      // Then make the API call
      await axios.post(
        `${API_URL}/api/sessions/${sessionId}/move-to-removed`,
        { submission_id: submissionId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      // WebSocket will broadcast queue_update - no need to refetch
    } catch (error) {
      console.error('Error moving to removed:', error);
      // Clear the protection since the API call failed
      recentlyRemovedSpotlightRef.current.delete(submissionId);
      // Revert optimistic update on error by rehydrating state
      rehydrateState();
    }
  };

  const playTrack = async (track) => {
    console.log('🎵 playTrack called:', track?.title || track?.track_title);
    
    if (!track) {
      console.error('🎵 playTrack - ERROR: No track provided!');
      return;
    }
    
    // CRITICAL: Unlock audio FIRST while we still have user gesture context
    // This MUST happen before any state changes on iOS Safari
    console.log('🔓 Unlocking audio on play (BEFORE state)...');
    try {
      await unlockAudio();
      console.log('🔓 Audio unlock completed');
    } catch (e) {
      console.error('🔓 Audio unlock failed:', e);
    }
    
    // NOW set state to trigger the player
    // Use flushSync to force synchronous re-render on iOS Safari
    console.log('🎵 playTrack - Setting currentTrack and isPlaying=true (flushSync)');
    
    flushSync(() => {
      // Reset player overlay notification counters when starting a new track
      if (playerSubmissionToastTimerRef.current) {
        clearTimeout(playerSubmissionToastTimerRef.current);
        playerSubmissionToastTimerRef.current = null;
      }
      playerSubmissionCountRef.current = 0;
      setPlayerSubmissionToast(null);
      setPlayerSkipToast(null);
      
      // CRITICAL: Increment playerMountKey to force UnifiedMusicPlayer to remount
      // This fixes Spotify second play bug - ensures fresh device registration
      setPlayerMountKey(prev => prev + 1);
      
      // Set track and playing state
      setCurrentTrack(track);
      setIsPlaying(true);
    });
    
    // Force a repaint on iOS Safari
    requestAnimationFrame(() => {
      document.body.offsetHeight; // Force reflow
    });
    
    // Notify backend that track is being played (non-blocking)
    if (isReviewer) {
      // Track for March Madness Challenge
      trackPlay(track.id);
      trackSubmissionPlayed(track.id);
      trackTrackChange(track.id, track.title || track.track_title);
      
      axios.post(
        `${API_URL}/api/sessions/${sessionId}/play`,
        { submission_id: track.id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      ).then(() => {
        console.log('🎵 playTrack - Backend notified successfully');
      }).catch((error) => {
        console.error('🎵 playTrack - Error notifying backend:', error);
      });
    }
  };
  
  const handleTrackEnd = useCallback(() => {
    // If in playlist mode, auto-advance to next track
    if (playlistMode && playlistQueue.length > 0) {
      const nextIndex = playlistIndex + 1;
      
      if (nextIndex < playlistQueue.length) {
        // Play next track in playlist
        const nextTrack = playlistQueue[nextIndex];
        setPlaylistIndex(nextIndex);
        
        // Reset notification counters for new track
        if (playerSubmissionToastTimerRef.current) {
          clearTimeout(playerSubmissionToastTimerRef.current);
          playerSubmissionToastTimerRef.current = null;
        }
        playerSubmissionCountRef.current = 0;
        setPlayerSubmissionToast(null);
        setPlayerSkipToast(null);
        
        setCurrentTrack(nextTrack);
        // isPlaying stays true
        console.log(`🎵 Playlist: Playing track ${nextIndex + 1}/${playlistQueue.length}: ${nextTrack.title}`);
      } else {
        // Playlist finished
        console.log('🎵 Playlist finished');
        setCurrentTrack(null);
        setIsPlaying(false);
        setPlaylistMode(false);
        setPlaylistQueue([]);
        setPlaylistIndex(0);
      }
    } else {
      // Normal mode - just close the player
      setCurrentTrack(null);
      setIsPlaying(false);
    }
    // No need to refetch - WebSocket handles all queue updates
  }, [playlistMode, playlistQueue, playlistIndex]);
  
  // Shuffle array utility function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  // Start playlist mode from a specific track in the Bangers/Spotlight list
  // CRITICAL: This is called from a user click, so we unlock audio here
  const startPlaylistMode = async (startIndex = 0, shuffle = false) => {
    console.log('🎵 startPlaylistMode called with startIndex:', startIndex, 'shuffle:', shuffle);
    const spotlightTracks = queue.spotlight || [];
    console.log('🎵 startPlaylistMode - spotlight tracks count:', spotlightTracks.length);
    
    if (spotlightTracks.length === 0) {
      console.error('🎵 startPlaylistMode - ERROR: No spotlight tracks!');
      return;
    }
    
    // CRITICAL: Unlock audio FIRST while we still have user gesture context
    // This MUST happen before any async operations or state changes
    console.log('🔓 Unlocking audio on Play All click (BEFORE state)...');
    try {
      await unlockAudio();
      console.log('🔓 Audio unlock completed for playlist');
    } catch (e) {
      console.error('🔓 Audio unlock failed for playlist:', e);
    }
    
    let playQueue;
    let actualStartIndex;
    
    if (shuffle) {
      // Shuffle the playlist, but start with the selected track
      const selectedTrack = spotlightTracks[startIndex];
      const otherTracks = spotlightTracks.filter((_, i) => i !== startIndex);
      playQueue = [selectedTrack, ...shuffleArray(otherTracks)];
      actualStartIndex = 0;
    } else {
      // Play in order starting from selected track
      playQueue = spotlightTracks.slice(startIndex);
      actualStartIndex = 0;
    }
    
    console.log('🎵 startPlaylistMode - play queue length:', playQueue.length);
    
    // Set ALL state to start playback
    // Use flushSync to force synchronous re-render on iOS Safari
    const firstTrack = playQueue[actualStartIndex];
    
    flushSync(() => {
      if (playerSubmissionToastTimerRef.current) {
        clearTimeout(playerSubmissionToastTimerRef.current);
        playerSubmissionToastTimerRef.current = null;
      }
      playerSubmissionCountRef.current = 0;
      setPlayerSubmissionToast(null);
      setPlayerSkipToast(null);
      setPlaylistQueue(playQueue);
      setPlaylistIndex(actualStartIndex);
      setPlaylistShuffle(shuffle);
      setPlaylistMode(true);
      setCurrentTrack(firstTrack);
      setIsPlaying(true);
      // CRITICAL: Increment playerMountKey to force fresh player mount
      setPlayerMountKey(prev => prev + 1);
    });
    
    // Force a repaint on iOS Safari
    requestAnimationFrame(() => {
      document.body.offsetHeight; // Force reflow
    });
    
    console.log(`🎵 Playlist started: ${playQueue.length} tracks, shuffle: ${shuffle}, first track: ${firstTrack?.title}`);
  };
  
  // Toggle shuffle mid-playlist
  // eslint-disable-next-line no-unused-vars
  const togglePlaylistShuffle = () => {
    if (!playlistMode) return;
    
    const currentTrackInQueue = playlistQueue[playlistIndex];
    const remainingTracks = playlistQueue.slice(playlistIndex + 1);
    
    if (playlistShuffle) {
      // Turn off shuffle - restore original order from spotlight
      const spotlightTracks = queue.spotlight || [];
      const currentIdx = spotlightTracks.findIndex(t => t.id === currentTrackInQueue.id);
      const newQueue = [currentTrackInQueue, ...spotlightTracks.slice(currentIdx + 1)];
      setPlaylistQueue(newQueue);
      setPlaylistIndex(0);
    } else {
      // Turn on shuffle - shuffle remaining tracks
      const shuffledRemaining = shuffleArray(remainingTracks);
      setPlaylistQueue([currentTrackInQueue, ...shuffledRemaining]);
      setPlaylistIndex(0);
    }
    
    setPlaylistShuffle(!playlistShuffle);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#F2D857] text-2xl">Loading session...</div>
      </div>
    );
  }

  // Show graceful error message if fetch failed
  if (fetchError) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-[#111] border border-[#F2D857]/30 rounded-lg p-8 text-center">
          <div className="text-[#F2D857] text-4xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-3">{fetchError.message}</h2>
          <p className="text-gray-400 mb-6">{fetchError.details}</p>
          
          {fetchError.type === 'not_found' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-4 mb-6">
              <p className="text-yellow-200 text-sm">
                <strong>Note:</strong> If you&apos;re seeing this error, it may be due to a temporary routing issue. 
                The backend services are operational, but direct session URLs may not be fully accessible yet.
              </p>
            </div>
          )}
          
          <div className="flex flex-col gap-3">
            <button
              onClick={() => window.location.reload()}
              className="bg-[#F2D857] text-black px-6 py-3 rounded-lg hover:bg-[#F2D857]/90 font-semibold transition"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="border border-gray-600 text-gray-300 px-6 py-3 rounded-lg hover:bg-gray-800 font-semibold transition"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-2xl">Session not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-black text-white pb-32">
      {/* Header - Reviewer Bio Panel */}
      <div className="bg-gradient-to-r from-[#F2D857] to-[#888888] p-4 sm:p-6 lg:p-8 relative w-full">
        {/* Subtle WebSocket connection status indicator - non-blocking, informational only */}
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
          <div 
            className={`w-2 h-2 rounded-full transition-colors duration-500 ${
              wsConnected 
                ? 'bg-green-500' 
                : 'bg-yellow-500 animate-pulse'
            }`}
            title={wsConnected ? 'Live' : 'Reconnecting...'}
          />
          <span className={`text-xs font-medium transition-opacity duration-500 ${
            wsConnected ? 'text-black/50' : 'text-black/70'
          }`}>
            {wsConnected ? 'Live' : 'Reconnecting...'}
          </span>
        </div>
        
        <div className="max-w-7xl mx-auto w-full">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-black flex items-center justify-center flex-shrink-0">
              {reviewer?.avatar ? (
                <img src={reviewer.avatar} alt={reviewer.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <Music className="w-10 h-10 sm:w-12 sm:h-12 text-[#F2D857]" />
              )}
            </div>
            <div className="flex-1 text-center sm:text-left w-full">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-2 break-words">{reviewer?.name}</h1>
              <p className="text-sm sm:text-base text-black/80 mb-3 break-words">{reviewer?.bio || 'Music Reviewer'}</p>
              
              {/* Social Media Icons */}
              <div className="flex gap-3 mb-3 justify-center sm:justify-start flex-wrap">
                {reviewer?.cash_app && (
                  <a 
                    href={formatCashAppUrl(reviewer.cash_app)}
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition transform hover:scale-110"
                    title="Cash App - Tip Me!"
                  >
                    <DollarSign className="w-5 h-5 text-white" />
                  </a>
                )}
                {reviewer?.instagram && (
                  <a 
                    href={reviewer.instagram.startsWith('http') ? reviewer.instagram : `https://instagram.com/${reviewer.instagram}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-black/80 transition transform hover:scale-110"
                    title="Instagram"
                  >
                    <Instagram className="w-5 h-5 text-[#F2D857]" />
                  </a>
                )}
                {reviewer?.youtube && (
                  <a 
                    href={reviewer.youtube.startsWith('http') ? reviewer.youtube : `https://youtube.com/@${reviewer.youtube}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-black/80 transition transform hover:scale-110"
                    title="YouTube"
                  >
                    <Youtube className="w-5 h-5 text-[#F2D857]" />
                  </a>
                )}
                {reviewer?.tiktok && (
                  <a 
                    href={reviewer.tiktok.startsWith('http') ? reviewer.tiktok : `https://tiktok.com/@${reviewer.tiktok}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-black/80 transition transform hover:scale-110"
                    title="TikTok"
                  >
                    <FaTiktok className="w-5 h-5 text-[#F2D857]" />
                  </a>
                )}
                {reviewer?.twitch && (
                  <a 
                    href={reviewer.twitch.startsWith('http') ? reviewer.twitch : `https://twitch.tv/${reviewer.twitch}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-black/80 transition transform hover:scale-110"
                    title="Twitch"
                  >
                    <TwitchIcon className="w-5 h-5 text-[#F2D857]" />
                  </a>
                )}
              </div>

              <div className="flex gap-4 text-sm text-black items-center justify-center sm:justify-start flex-wrap">
                <span>★ {reviewer?.rating?.average || reviewer?.average_rating || 0} Rating</span>
                <span>{reviewer?.totalReviews || 0} Reviews</span>
                {reviewer?.is_live && (
                  <span className="px-3 py-1 bg-red-500 text-white rounded-full text-xs font-bold">
                    🔴 LIVE
                  </span>
                )}
                
                {/* Live Streaming Platform Toggles (Reviewer Only) */}
                {isReviewer && reviewer?.is_live && (
                  <div className="w-full flex flex-col items-center sm:items-start mt-1" data-testid="live-streaming-toggles">
                    <span className="text-[10px] uppercase tracking-widest text-gray-500 mb-1.5 font-medium">
                      Tap to show where you're live
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleLiveStreamingPlatform('youtube')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          liveStreamingPlatforms.includes('youtube')
                            ? 'bg-red-600 text-white ring-2 ring-red-400 ring-offset-1 scale-110'
                            : 'bg-white/60 text-gray-400 hover:bg-white/80 hover:text-gray-600'
                        }`}
                        title={liveStreamingPlatforms.includes('youtube') ? 'Live on YouTube — click to remove' : 'Click to show you\'re live on YouTube'}
                        data-testid="toggle-youtube"
                      >
                        <Youtube className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleLiveStreamingPlatform('tiktok')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          liveStreamingPlatforms.includes('tiktok')
                            ? 'bg-black text-white ring-2 ring-gray-400 ring-offset-1 scale-110'
                            : 'bg-white/60 text-gray-400 hover:bg-white/80 hover:text-gray-600'
                        }`}
                        title={liveStreamingPlatforms.includes('tiktok') ? 'Live on TikTok — click to remove' : 'Click to show you\'re live on TikTok'}
                        data-testid="toggle-tiktok"
                      >
                        <FaTiktok className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => toggleLiveStreamingPlatform('twitch')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          liveStreamingPlatforms.includes('twitch')
                            ? 'bg-purple-600 text-white ring-2 ring-purple-400 ring-offset-1 scale-110'
                            : 'bg-white/60 text-gray-400 hover:bg-white/80 hover:text-gray-600'
                        }`}
                        title={liveStreamingPlatforms.includes('twitch') ? 'Live on Twitch — click to remove' : 'Click to show you\'re live on Twitch'}
                        data-testid="toggle-twitch"
                      >
                        <TwitchIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleLiveStreamingPlatform('instagram')}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                          liveStreamingPlatforms.includes('instagram')
                            ? 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white ring-2 ring-pink-400 ring-offset-1 scale-110'
                            : 'bg-white/60 text-gray-400 hover:bg-white/80 hover:text-gray-600'
                        }`}
                        title={liveStreamingPlatforms.includes('instagram') ? 'Live on Instagram — click to remove' : 'Click to show you\'re live on Instagram'}
                        data-testid="toggle-instagram"
                      >
                        <Instagram className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Session Controls (Reviewer Only) */}
      {isReviewer && (
        <div className="bg-[#111] border-b border-[#F2D857]/20 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-bold mb-4 text-[#F2D857]">Session Controls</h2>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <button
                onClick={toggleSubmissions}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition ${
                  session.submissions_enabled
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Submissions: {session.submissions_enabled ? 'ON' : 'OFF'}
              </button>
              <button
                onClick={toggleSkipsOnly}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition ${
                  session.skips_only_mode
                    ? 'bg-[#F2D857] text-black hover:bg-[#F2D857]/90'
                    : 'bg-[#888888] hover:bg-[#888888]/90'
                }`}
              >
                Skips Only: {session.skips_only_mode ? 'ON' : 'OFF'}
              </button>
              
              {/* Soundboard Button - Desktop Only, positioned after Skips Only */}
              <button
                onClick={() => {
                  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                  if (isMobile) {
                    toast({
                      title: "Desktop Only",
                      description: "Soundboard is only available on desktop devices",
                      duration: 3000
                    });
                    return;
                  }
                  const width = 320;
                  const height = 520;
                  const left = window.screenX + window.outerWidth - width - 50;
                  const top = window.screenY + 100;
                  // Check if user is PRO - subscriptionActive is set for PRO users
                  const isPro = reviewer?.subscriptionActive === true || reviewer?.planTier === 'paid';
                  window.open(
                    `/soundboard.html?pro=${isPro ? '1' : '0'}`,
                    'Soundboard',
                    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
                  );
                }}
                className="hidden md:flex w-full sm:w-auto items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                title="Open Soundboard (Desktop Only)"
              >
                <Volume2 className="w-4 h-4" />
                Soundboard
                <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded">PRO</span>
              </button>
              
              {/* Copy Session Link Button */}
              <button
                onClick={() => {
                  const sessionUrl = `${window.location.origin}/r/${reviewer?.username || reviewerSlug}`;
                  navigator.clipboard.writeText(sessionUrl);
                  toast({
                    title: "🔗 Session Link Copied!",
                    description: "Share with artists to receive submissions",
                    duration: 3000
                  });
                }}
                className="w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition border-2 border-[#F2D857] text-[#F2D857] hover:bg-[#F2D857] hover:text-black flex items-center justify-center gap-2"
              >
                <Link className="w-4 h-4" />
                Copy Session Link
              </button>
              
              {/* Live Mobile Button - Screen Broadcast Mode (Mobile Only) */}
              {/* Hidden on desktop (md:hidden), shown on mobile devices only */}
              {/* Now opens onboarding modal first before entering Live Mobile */}
              <button
                onClick={() => setShowLiveMobileOnboarding(true)}
                className="md:hidden w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white flex items-center justify-center gap-2"
                data-testid="live-mobile-toggle"
              >
                <Smartphone className="w-4 h-4" />
                Live Mobile
              </button>
              
              {/* Notification Bell with Count */}
              {notificationCount > 0 && (
                <div className="flex items-center justify-center gap-2 px-4 py-3 bg-[#222] rounded-lg">
                  <Bell className="w-5 h-5 text-[#F2D857]" />
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {notificationCount} paid
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Skip/Superskip/Custom Notification Toast (Both Reviewer AND Public View) */}
      {skipNotification && (
        <div className="fixed top-4 right-4 z-[60] animate-pulse">
          <div className={`px-6 py-4 rounded-lg shadow-lg border-2 ${
            skipNotification.type === 'custom' ? 'bg-green-500 border-green-300' :
            skipNotification.type === 'superskip' ? 'bg-red-500 border-red-300' :
            'bg-yellow-500 border-yellow-300'
          }`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{skipNotification.emoji}</span>
              <div>
                <p className={`font-bold text-lg ${skipNotification.type === 'skip' ? 'text-black' : 'text-white'}`}>
                  NEW {skipNotification.label}!
                </p>
                <p className={`text-sm ${skipNotification.type === 'skip' ? 'text-black/80' : 'text-white/80'}`}>
                  {skipNotification.title} - {skipNotification.artist}
                </p>
              </div>
              <button 
                onClick={() => setSkipNotification(null)}
                className={`ml-4 ${skipNotification.type === 'skip' ? 'text-black/60 hover:text-black' : 'text-white/60 hover:text-white'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Music Button (Public Users Only) */}
      {!isReviewer && (
        <div className="max-w-7xl mx-auto px-6 mt-6">
          <button
            data-testid="submit-music-btn"
            onClick={() => window.location.href = `/upload?reviewer=${session.reviewer_id}&session=${sessionId}`}
            className="bg-[#F2D857] text-black font-semibold py-3 px-8 rounded-lg hover:bg-[#F2D857]/90 transition flex items-center gap-2 text-base"
          >
            <Music className="w-5 h-5" />
            Submit Your Music
          </button>
        </div>
      )}

      {/* Pending Payment Verifications - Only for PAID plan reviewers with Cash App */}
      {isReviewer && session && reviewer?.canShowCashApp && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-6">
          <PendingPaymentVerifications 
            reviewerId={session.reviewer_id} 
            sessionId={sessionId}
          />
        </div>
      )}

      {/* Queue Tabs */}
      <div className="w-full px-4 sm:px-6 mt-6 sm:mt-8" ref={tabsRef}>
        <div className="max-w-7xl mx-auto">
          {/* Public users see only "My Submissions", Reviewers see all tabs */}
          {isReviewer ? (
            // Reviewer View - All Tabs (Horizontal Scroll on Mobile)
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="flex gap-1 border-b border-[#F2D857]/20 mb-6 sm:mb-8 min-w-max sm:min-w-0">
                <button
                  onClick={() => setActiveTab('upcoming')}
                  className={`px-4 sm:px-6 py-3 font-semibold transition whitespace-nowrap text-sm sm:text-base ${
                    activeTab === 'upcoming'
                      ? 'border-b-2 border-[#F2D857] text-[#F2D857]'
                      : 'text-[#888888] hover:text-white'
                  }`}
                >
                  Upcoming Tracks ({queue.upcoming?.length || 0})
                </button>
                
                {/* Spotlight Tab with Editable Name */}
                <div className={`relative ${
                  activeTab === 'spotlight'
                    ? 'border-b-2 border-[#F2D857]'
                    : ''
                }`}>
                  {editingSpotlightName ? (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <input
                        type="text"
                        value={spotlightName}
                        onChange={(e) => setSpotlightName(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            updateSpotlightName(spotlightName);
                          }
                        }}
                        className="bg-[#222] border border-[#F2D857] rounded px-2 py-1 text-white text-sm w-28 sm:w-32"
                        placeholder="Enter name"
                        autoFocus
                      />
                      <button
                        onClick={() => updateSpotlightName(spotlightName)}
                        className="text-green-500 hover:text-green-400 text-xs"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => setEditingSpotlightName(false)}
                        className="text-red-500 hover:text-red-400 text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setActiveTab('spotlight')}
                      onDoubleClick={() => {
                        setSpotlightName(session.spotlight_queue_name || 'Spotlight');
                        setEditingSpotlightName(true);
                      }}
                      className={`px-4 sm:px-6 py-3 font-semibold transition flex items-center gap-2 whitespace-nowrap text-sm sm:text-base ${
                        activeTab === 'spotlight'
                          ? 'text-[#F2D857]'
                          : 'text-[#888888] hover:text-white'
                      }`}
                      title="Double-click to rename"
                    >
                      {session?.spotlight_queue_name || 'Spotlight'} ({queue.spotlight?.length || 0})
                      <Pencil className="w-3 h-3 opacity-50" />
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => setActiveTab('finished')}
                  className={`px-4 sm:px-6 py-3 font-semibold transition whitespace-nowrap text-sm sm:text-base ${
                    activeTab === 'finished'
                  ? 'border-b-2 border-[#F2D857] text-[#F2D857]'
                  : 'text-[#888888] hover:text-white'
              }`}
            >
              Finished Tracks ({queue.finished?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('removed')}
              className={`px-4 sm:px-6 py-3 font-semibold transition whitespace-nowrap text-sm sm:text-base ${
                activeTab === 'removed'
                  ? 'border-b-2 border-[#F2D857] text-[#F2D857]'
                  : 'text-[#888888] hover:text-white'
              }`}
            >
              Removed Tracks ({queue.removed?.length || 0})
            </button>
          </div>
        </div>
      ) : (
          // Public View - No header needed here, Zone A has its own header
          null
        )}
      </div>

      {/* Track List */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="space-y-4">
          {isReviewer ? (
            // Reviewer View - Show all tabs and allow actions
            <>
              {activeTab === 'upcoming' && (
                queue.upcoming?.length > 0 ? (
                  queue.upcoming.map((track) => (
                    <TrackCard
                      key={track.id}
                      track={track}
                      isReviewer={isReviewer}
                      onPlay={() => playTrack(track)}
                      onSpotlight={() => addToSpotlight(track.id)}
                      onFinish={() => moveToFinished(track.id)}
                      onRemove={() => moveToRemoved(track.id)}
                      showActions={true}
                    />
                  ))
                ) : loading ? (
                  // Shimmer loading placeholder
                  <ShimmerTrackCard />
                ) : (
                  <div className="text-center text-[#888888] py-12">No upcoming tracks</div>
                )
              )}
              
              {activeTab === 'spotlight' && (
                <>
                  {/* Playlist Controls for Bangers/Spotlight */}
                  {queue.spotlight?.length > 0 && (
                    <div className="mb-6 bg-[#111] border border-[#F2D857]/30 rounded-lg p-4">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <span className="text-[#F2D857] font-semibold text-sm">Playlist Mode</span>
                          <span className="text-gray-500 text-xs">Auto-play through {session?.spotlight_queue_name || 'Bangers'}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Shuffle Toggle */}
                          <button
                            onClick={() => setPlaylistShuffle(!playlistShuffle)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                              playlistShuffle 
                                ? 'bg-[#F2D857] text-black' 
                                : 'bg-[#222] text-gray-400 hover:text-white border border-gray-700'
                            }`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Shuffle {playlistShuffle ? 'On' : 'Off'}
                          </button>
                          
                          {/* Play All Button */}
                          <button
                            onClick={() => {
                              console.log('🎵 PLAY ALL CLICKED');
                              startPlaylistMode(0, playlistShuffle);
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              console.log('🎵 PLAY ALL TOUCHED');
                              startPlaylistMode(0, playlistShuffle);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-[#F2D857] text-black rounded-lg font-semibold hover:bg-[#F2D857]/90 transition active:bg-green-500"
                            style={{ touchAction: 'manipulation' }}
                          >
                            <Play className="w-4 h-4" />
                            Play All ({queue.spotlight.length})
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Spotlight Track List */}
                  {queue.spotlight?.length > 0 ? (
                    queue.spotlight.map((track, index) => (
                      <TrackCard
                        key={track.id}
                        track={track}
                        isReviewer={isReviewer}
                        onPlay={() => playTrack(track)}
                        onRemove={() => setDeleteConfirmTrack(track)}
                        showActions={true}
                        isSpotlighted={true}
                        spotlightOnly={true}
                        playlistIndex={index + 1}
                        totalInPlaylist={queue.spotlight.length}
                      />
                    ))
                  ) : loading ? (
                    <ShimmerTrackCard />
                  ) : (
                    <div className="text-center text-[#888888] py-12">No spotlight tracks</div>
                  )}
                </>
              )}
              
              {activeTab === 'finished' && (
                queue.finished?.length > 0 ? (
                  queue.finished.map((track) => (
                    <TrackCard 
                      key={track.id} 
                      track={track} 
                      isReviewer={isReviewer}
                      onPlay={() => playTrack(track)}
                      onSpotlight={() => addToSpotlight(track.id)}
                      onRemove={() => moveToRemoved(track.id)}
                      showActions={true}
                      showFinishButton={false}
                      isSpotlighted={track.is_spotlighted}
                    />
                  ))
                ) : loading ? (
                  // Shimmer loading placeholder
                  <ShimmerTrackCard />
                ) : (
                  <div className="text-center text-[#888888] py-12">No finished tracks</div>
                )
              )}
              
              {activeTab === 'removed' && (
                queue.removed?.length > 0 ? (
                  queue.removed.map((track) => (
                    <TrackCard
                      key={track.id}
                      track={track}
                      isReviewer={isReviewer}
                      onPlay={() => playTrack(track)}
                      showActions={false}
                      isRemoved={true}
                    />
                  ))
                ) : loading ? (
                  // Shimmer loading placeholder
                  <ShimmerTrackCard />
                ) : (
                  <div className="text-center text-[#888888] py-12">No removed tracks</div>
                )
              )}
            </>
          ) : (
            // Public View - Zone A and Zone B only
            <>
              {/* Zone A - Live Submission Feed (original design with new functionality) */}
              <div className="mb-8">
                {/* Header with yellow underline */}
                <h3 className="text-[#F2D857] font-semibold text-lg mb-1">Live Submission Feed</h3>
                <div className="h-0.5 bg-[#F2D857] mb-6"></div>
                
                {liveSubmission ? (
                  /* Active live card - shows for 7 seconds */
                  <div 
                    key={liveSubmission.id}
                    className="bg-gradient-to-r from-[#F2D857]/20 to-transparent border border-[#F2D857]/40 rounded-lg p-4"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{liveSubmission.platform_icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{liveSubmission.title}</p>
                        <p className="text-sm text-gray-400 truncate">by {liveSubmission.artist}</p>
                      </div>
                      <span className="text-xs text-[#F2D857] whitespace-nowrap">Just now</span>
                    </div>
                  </div>
                ) : (
                  /* Empty state - waiting for submissions */
                  <div className="text-center py-12">
                    <Music className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                    <p className="text-lg font-semibold text-gray-400 mb-2">Waiting for submissions...</p>
                    <p className="text-sm text-gray-500 mb-6">New submissions will appear here in real-time</p>
                    <p className="text-sm text-gray-600">Loading next submission...</p>
                  </div>
                )}
              </div>
              
              {/* Zone B - Recent Submissions (rolling top-10 list, newest first) */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <span>Recent Submissions</span>
                  <span className="text-xs text-gray-500 font-normal">({Math.min(recentSubmissions.length, 10)} of 10)</span>
                </h3>
                
                {recentSubmissions.length > 0 ? (
                  <div className="space-y-2">
                    {recentSubmissions.slice(0, 10).map((submission, index) => (
                      <div 
                        key={`recent-${submission.id}-${index}`}
                        className="flex items-center gap-3 p-3 bg-[#111] rounded-lg border border-gray-800 hover:border-gray-700 transition-colors"
                      >
                        {/* Position number */}
                        <span className="text-xs text-gray-500 w-6 text-center flex-shrink-0">#{index + 1}</span>
                        
                        {/* Platform icon */}
                        <span className="text-xl">{submission.platform_icon}</span>
                        
                        {/* Track info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate text-sm">{submission.title}</p>
                          <p className="text-xs text-gray-400 truncate">by {submission.artist}</p>
                        </div>
                        
                        {/* Genre badge */}
                        {submission.genre && (
                          <span className="text-xs bg-gray-800 text-gray-400 px-2 py-1 rounded hidden sm:inline">
                            {submission.genre}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : loading ? (
                  // Loading state
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-14 bg-[#111] rounded-lg animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  // Empty state
                  <div className="text-center text-gray-400 py-8 bg-[#111] rounded-lg border border-gray-800">
                    <p className="mb-1">No submissions yet</p>
                    <p className="text-sm text-gray-500">Be the first to submit a track!</p>
                  </div>
                )}
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Rating Widget for Public Viewers - Only show after session ends or when no tracks playing */}
      {!isReviewer && session && reviewer && !currentTrack && (
        <div className="max-w-7xl mx-auto px-6 mt-8 mb-8">
          <div className="bg-[#111] border border-[#F2D857]/20 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-2 text-center">How was this session?</h3>
            <RatingWidget 
              sessionId={sessionId}
              reviewerId={session.reviewer_id}
              currentRating={reviewer.average_rating || 0}
            />
          </div>
        </div>
      )}

      {/* Music Player - Modal Overlay for better visibility */}
      {/* HIDE completely when Live Mobile is active - player is rendered inline there */}
      {currentTrack && isPlaying && !liveMobileMode && (
        <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center p-8 bg-black/80 z-50">
          {/* === PLAYER OVERLAY NOTIFICATIONS === */}
          {/* These appear ON TOP of the player, always visible while music plays */}
          
          {/* LEFT SIDE: New Submission Toast */}
          {playerSubmissionToast && isReviewer && (
            <div 
              className="fixed top-20 left-4 z-[60] animate-in slide-in-from-left duration-300"
              style={{ animation: 'slideInLeft 0.3s ease-out' }}
            >
              <div className="bg-blue-600/95 backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-xl border border-blue-400/50 flex items-center gap-3">
                <div className="bg-white/20 rounded-full p-2">
                  <Music className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-bold">
                    {playerSubmissionToast.count} New Submission{playerSubmissionToast.count > 1 ? 's' : ''}
                  </div>
                  <div className="text-xs opacity-80">Added to queue</div>
                </div>
              </div>
            </div>
          )}
          
          {/* RIGHT SIDE: Skip/Paid Submission Toast (Yellow themed) */}
          {playerSkipToast && isReviewer && (
            <div 
              className="fixed top-20 right-4 z-[60] animate-in slide-in-from-right duration-300"
              style={{ animation: 'slideInRight 0.3s ease-out' }}
            >
              <div className={`backdrop-blur-sm text-white px-4 py-3 rounded-lg shadow-xl border flex items-center gap-3 ${
                playerSkipToast.type === 'custom' 
                  ? 'bg-green-600/95 border-green-400/50' 
                  : playerSkipToast.type === 'superskip'
                    ? 'bg-red-600/95 border-red-400/50'
                    : 'bg-yellow-500/95 border-yellow-400/50'
              }`}>
                <div className="text-2xl">{playerSkipToast.emoji}</div>
                <div>
                  <div className="text-sm font-bold">NEW {playerSkipToast.label}!</div>
                  <div className="text-xs opacity-90 max-w-[180px] truncate">
                    {playerSkipToast.title} - {playerSkipToast.artist}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* FLOATING "Go to Upcoming" BUTTON - Always visible on player */}
          {isReviewer && (
            <button
              onClick={() => {
                // Close the player and go to Upcoming tab
                setCurrentTrack(null);
                setIsPlaying(false);
                setPlaylistMode(false);
                setActiveTab('upcoming');
                // Scroll to tabs
                setTimeout(() => {
                  tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
              }}
              className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold px-3 py-2 sm:px-4 sm:py-3 rounded-full shadow-xl flex items-center gap-1.5 sm:gap-2 transition-all hover:scale-105 text-sm sm:text-base max-w-[calc(100vw-2rem)] sm:max-w-none"
            >
              <span className="whitespace-nowrap">Upcoming</span>
              <span className="bg-black/20 rounded-full px-1.5 sm:px-2 py-0.5 text-xs sm:text-sm min-w-[1.5rem] text-center">
                {queue.upcoming?.length || 0}
              </span>
            </button>
          )}
          
          {/* Player card - COMPACT when Live Mobile (visible small bar at bottom) */}
          <div className={`bg-[#0a0a0a] border-2 border-[#F2D857] rounded-lg shadow-2xl ${
            liveMobileMode 
              ? 'w-full max-w-md p-2' 
              : 'w-full max-w-5xl max-h-[90vh] overflow-auto'
          }`}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-[#F2D857]/30">
              <div className="flex items-center gap-3">
                <Music className="w-6 h-6 text-[#F2D857]" />
                <div>
                  <h3 className="text-lg font-bold text-white">{currentTrack.title}</h3>
                  <p className="text-sm text-gray-400">{currentTrack.artist}</p>
                  <p className="text-xs text-gray-500">Platform: {currentTrack.platform} | ID: {currentTrack.platform_id}</p>
                </div>
                {/* Playlist Mode Indicator */}
                {playlistMode && (
                  <div className="ml-4 bg-[#F2D857]/20 text-[#F2D857] px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                    <Play className="w-3 h-3" />
                    Playlist Mode {playlistShuffle && '(Shuffle)'}
                    <span className="opacity-70">• {playlistIndex + 1}/{playlistQueue.length}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setCurrentTrack(null);
                  setIsPlaying(false);
                  setPlaylistMode(false);
                }}
                className="text-white hover:text-[#F2D857] transition p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Player - Compact in Live Mobile mode */}
            <div className={liveMobileMode ? 'p-1' : 'p-6'}>
              {console.log('Rendering UnifiedMusicPlayer with track:', currentTrack, 'autoPlay:', isPlaying || playlistMode)}
              <UnifiedMusicPlayer 
                key={`${currentTrack?.id || currentTrack?.platform_id}-${playlistIndex}-${playerMountKey}`}
                track={currentTrack} 
                onTrackEnd={handleTrackEnd}
                isReviewer={isReviewer}
                autoPlay={isPlaying || playlistMode}
                compactMode={liveMobileMode}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* One-time animated nudge for public users (artists) */}
      {/* Only shows if: not a reviewer, skips enabled, has active queue, once per session */}
      {/* IMPORTANT: Hide when HotSeatPrompt is visible to prevent overlap */}
      {!isReviewer && !showHotSeatPrompt && (
        <SessionNudge 
          skipsEnabled={!session?.skips_only_mode || session?.skips_only_mode} // Show regardless of skips_only_mode since skips are always available
          hasActiveQueue={queue.upcoming?.length > 0 || queue.spotlight?.length > 0}
          sessionId={sessionId}
        />
      )}
      
      {/* Hot Seat Prompt - Only for submitting artist after successful submission */}
      {showHotSeatPrompt && hotSeatSubmission && (
        <HotSeatPrompt
          submissionData={hotSeatSubmission}
          sessionUrl={window.location.href}
          onDismiss={() => setShowHotSeatPrompt(false)}
          onPosted={(post) => {
            console.log('Posted to Hot Seat:', post);
          }}
        />
      )}
      
      {/* FLOATING "Go to Upcoming" Button - Visible when in Bangers tab (outside player) */}
      {isReviewer && activeTab === 'spotlight' && !currentTrack && queue.spotlight?.length > 3 && (
        <button
          onClick={() => {
            setActiveTab('upcoming');
            tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold px-3 py-2 sm:px-4 sm:py-3 rounded-full shadow-xl flex items-center gap-1.5 sm:gap-2 transition-all hover:scale-105 text-sm sm:text-base max-w-[calc(100vw-2rem)] sm:max-w-none"
        >
          <span className="whitespace-nowrap">Upcoming</span>
          <span className="bg-black/20 rounded-full px-1.5 sm:px-2 py-0.5 text-xs sm:text-sm min-w-[1.5rem] text-center">
            {queue.upcoming?.length || 0}
          </span>
        </button>
      )}
      
      {/* DELETE CONFIRMATION DIALOG - For Bangers tab track removal */}
      <AlertDialog open={!!deleteConfirmTrack} onOpenChange={(open) => !open && setDeleteConfirmTrack(null)}>
        <AlertDialogContent className="bg-[#111] border border-[#F2D857]/30 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#F2D857]">
              <AlertTriangle className="w-5 h-5" />
              Remove from Bangers?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              {deleteConfirmTrack && (
                <>
                  Are you sure you want to remove <span className="font-semibold text-white">"{deleteConfirmTrack.title || deleteConfirmTrack.track_title}"</span> by <span className="font-semibold text-white">{deleteConfirmTrack.artist}</span> from your Bangers list?
                  <br /><br />
                  <span className="text-gray-400">This track will be moved to the Removed Tracks tab.</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#222] border-[#333] text-white hover:bg-[#333] hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteConfirmTrack) {
                  moveToRemoved(deleteConfirmTrack.id);
                  setDeleteConfirmTrack(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white border-0"
            >
              Yes, Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* LIVE MOBILE ONBOARDING MODAL - Instructions before entering Live Mobile */}
      <LiveMobileOnboardingModal
        isOpen={showLiveMobileOnboarding}
        onClose={() => setShowLiveMobileOnboarding(false)}
        onGoMobile={() => setLiveMobileMode(true)}
      />
      
      {/* LIVE MOBILE VIEW - Full-screen broadcast mode for reviewers */}
      {liveMobileMode && isReviewer && (
        <LiveMobileView
          reviewer={reviewer}
          session={session}
          queue={queue}
          currentTrack={currentTrack}
          isPlaying={isPlaying}
          playlistMode={playlistMode}
          playlistQueue={playlistQueue}
          playlistIndex={playlistIndex}
          wsConnected={wsConnected}
          // Pass state setters directly
          setCurrentTrack={setCurrentTrack}
          setIsPlaying={setIsPlaying}
          setPlaylistMode={setPlaylistMode}
          setPlaylistQueue={setPlaylistQueue}
          setPlaylistIndex={setPlaylistIndex}
          // Action functions
          addToSpotlight={addToSpotlight}
          moveToFinished={moveToFinished}
          moveToRemoved={moveToRemoved}
          startPlaylistMode={startPlaylistMode}
          onClose={() => setLiveMobileMode(false)}
          skipNotification={skipNotification}
        >
          {/* Inline Player for Live Mobile */}
          {currentTrack && (
            <UnifiedMusicPlayer 
              key={`live-mobile-${currentTrack?.id || currentTrack?.platform_id}-${playlistIndex}-${playerMountKey}`}
              track={currentTrack} 
              onTrackEnd={handleTrackEnd}
              isReviewer={isReviewer}
              autoPlay={isPlaying || playlistMode}
              compactMode={true}
              externalIsPlaying={isPlaying}
            />
          )}
        </LiveMobileView>
      )}
      
      {/* GO LIVE GUIDE TOUR - Shows for first-time reviewers */}
      {isReviewer && (
        <>
          <GoLiveGuideTour
            key={tourResetKey}
            isOpen={showGoLiveTour}
            onClose={() => setShowGoLiveTour(false)}
            onComplete={() => setShowGoLiveTour(false)}
          />
          
          {/* Floating Guide Button - Always accessible for reviewers */}
          {!showGoLiveTour && !liveMobileMode && (
            <FloatingGuideButton
              onOpenTour={() => setShowGoLiveTour(true)}
              onStartFresh={handleOpenTourFresh}
              onOpenGuidePage={handleOpenGuidePage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SessionDashboard;

